import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import FileAttachmentUpload from './FileAttachmentUpload';
import { MeetingFormData } from '../models/Content';
import { Attachment } from '../models/Content';
import { TIMEZONE_OPTIONS, getCurrentDateFormat } from '../config/settings';
import { formatDateForInput } from '../utils/dateUtils';

interface MeetingEditFormProps {
  formData: MeetingFormData;
  attachments: Attachment[];
  isEditing: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
}

const meetingTypes = [
  { value: 'AGM', label: 'Annual General Meeting' },
  { value: 'EGM', label: 'Extraordinary General Meeting' },
  { value: 'Information', label: 'Information Meeting' },
  { value: 'Norway AGM', label: 'Norway Annual General Meeting' },
  { value: 'Norway EGM', label: 'Norway Extraordinary General Meeting' },
  { value: 'Hybrid', label: 'Hybrid' }
] as const;

const venues = [
  { value: 'Virtual', label: 'Virtual' },
  { value: 'In-Person', label: 'In-Person' },
  { value: 'Hybrid', label: 'Hybrid' }
] as const;

const MeetingEditForm: React.FC<MeetingEditFormProps> = ({
  formData,
  attachments,
  isEditing,
  onInputChange,
  onSubmit,
  onCancel,
  setAttachments
}) => {
  return (
    <Card className="p-6 mb-8">
      <h2 className="text-2xl font-semibold mb-4">{isEditing ? 'Edit Meeting' : 'New Meeting'}</h2>
      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">Title</label>
            <Input
              type="text"
              name="title"
              id="title"
              value={formData.title}
              onChange={onInputChange}
              required
            />
          </div>
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700">Meeting Type</label>
            <select
              name="type"
              id="type"
              value={formData.type}
              onChange={onInputChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              {meetingTypes.map(mt => (
                <option key={mt.value} value={mt.value}>{mt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700">
              Date {getCurrentDateFormat() === 'yyyy-mm-dd' ? '(YYYY-MM-DD)' : 
                    getCurrentDateFormat() === 'mm/dd/yyyy' ? '(MM/DD/YYYY)' : '(DD/MM/YYYY)'}
            </label>
            <Input
              type="text"
              name="date"
              id="date"
              value={formData.date}
              onChange={onInputChange}
              placeholder={getCurrentDateFormat() === 'yyyy-mm-dd' ? 'YYYY-MM-DD' : 
                          getCurrentDateFormat() === 'mm/dd/yyyy' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'}
              pattern={getCurrentDateFormat() === 'yyyy-mm-dd' ? '\\d{4}-\\d{2}-\\d{2}' : 
                      getCurrentDateFormat() === 'mm/dd/yyyy' ? '\\d{2}/\\d{2}/\\d{4}' : '\\d{2}/\\d{2}/\\d{4}'}
              required
            />
          </div>
          <div>
            <label htmlFor="time" className="block text-sm font-medium text-gray-700">Time</label>
            <Input
              type="time"
              name="time"
              id="time"
              value={formData.time}
              onChange={onInputChange}
              required
            />
          </div>
          <div>
            <label htmlFor="timezone" className="block text-sm font-medium text-gray-700">Timezone</label>
            <select
              name="timezone"
              id="timezone"
              value={formData.timezone}
              onChange={onInputChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              required
            >
              {TIMEZONE_OPTIONS.map(tz => (
                <option key={tz.value} value={tz.value}>
                  {tz.label} ({tz.offset})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="venue" className="block text-sm font-medium text-gray-700">Venue</label>
            <select
              name="venue"
              id="venue"
              value={formData.venue}
              onChange={onInputChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              {venues.map(v => (
                <option key={v.value} value={v.value}>{v.label}</option>
              ))}
            </select>
          </div>
          {formData.venue !== 'Virtual' && (
            <div>
              <label htmlFor="physicalVenue" className="block text-sm font-medium text-gray-700">Physical Venue Address</label>
              <Input
                type="text"
                name="physicalVenue"
                id="physicalVenue"
                value={formData.physicalVenue}
                onChange={onInputChange}
                placeholder="e.g., 123 Main St, Anytown"
              />
            </div>
          )}
        </div>

        {formData.venue !== 'In-Person' && (
          <div>
            <label htmlFor="meetingLink" className="block text-sm font-medium text-gray-700">Meeting Link</label>
            <Input
              type="url"
              name="meetingLink"
              id="meetingLink"
              value={formData.meetingLink}
              onChange={onInputChange}
              placeholder="https://example.com/meeting"
            />
          </div>
        )}

        <div>
          <label htmlFor="agenda" className="block text-sm font-medium text-gray-700">Agenda</label>
          <Textarea
            name="agenda"
            id="agenda"
            value={formData.agenda}
            onChange={onInputChange}
            rows={4}
            placeholder="Enter the meeting agenda here."
          />
        </div>

        <div>
          <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700">Additional Information</label>
          <Textarea
            name="additionalInfo"
            id="additionalInfo"
            value={formData.additionalInfo}
            onChange={onInputChange}
            rows={3}
            placeholder="Any other relevant details."
          />
        </div>
        
        <FileAttachmentUpload
          attachments={attachments}
          setAttachments={setAttachments}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">Contact Email</label>
            <Input
              type="email"
              name="contactEmail"
              id="contactEmail"
              value={formData.contactEmail}
              onChange={onInputChange}
            />
          </div>
          <div>
            <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">Contact Phone</label>
            <Input
              type="tel"
              name="contactPhone"
              id="contactPhone"
              value={formData.contactPhone}
              onChange={onInputChange}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            className="h-8 px-3 text-sm"
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="outline"
            className="h-8 px-3 text-sm"
          >
            {isEditing ? 'Update Meeting' : 'Post Meeting'}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default MeetingEditForm; 